import Api from "../services/AxiosService";
import { unwrap } from "./envelope";
import type { AuthUser, RoleCode } from "../types/auth-types/AuthUser";

/**
 * Query params for `GET /v1/users` (svc-03-user-management).
 * Pagination is offset-based (skip/take); the endpoint returns a plain
 * `User[]` with NO total count, so callers detect "has next page" by whether
 * a full page (`rows.length === take`) came back. `search` is matched
 * server-side across name/email. Region-scoping is enforced by the repository
 * layer (§D.3) — admins see all regions, other can_manage_users callers see
 * their own.
 */
export type ListUsersParams = {
  skip?: number;
  take?: number;
  search?: string;
  /**
   * User-type filter. `true` = internal (admin-app) users, `false` =
   * external-app users, omitted = all. Axios serialises the boolean to
   * "true"/"false"; svc-03 parses it back (undefined ⇒ "All").
   */
  isInternal?: boolean;
  /** Only users whose ACTIVE role has this `role_master.code`; omitted = all. */
  roleCode?: string;
};

/** The public user row returned by `GET /v1/users` (same shape as AuthUser). */
export type AdminUser = AuthUser;

export async function listUsers(
  params: ListUsersParams = {},
): Promise<AdminUser[]> {
  // Axios omits `undefined` params; only send `search` when it has content.
  const res = await Api.get("/v1/users", { params });
  return unwrap<AdminUser[]>(res.data);
}

/**
 * Read every page for a narrow user filter. Intended for bounded option lists
 * such as the Responsibility Map recipient selector; ordinary tables should
 * continue to page with `listUsers`.
 */
export async function listAllUsers(
  params: Omit<ListUsersParams, "skip" | "take"> = {},
): Promise<AdminUser[]> {
  const take = 200;
  const maxPages = 100;
  const users: AdminUser[] = [];

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    const skip = pageIndex * take;
    const page = await listUsers({ ...params, skip, take });
    users.push(...page);
    if (page.length < take) return users;
  }

  throw new Error("User option query exceeded the 20,000-row safety limit.");
}

export async function getUser(id: string): Promise<AdminUser> {
  const res = await Api.get(`/v1/users/${id}`);
  return unwrap<AdminUser>(res.data);
}

/**
 * Body of `PATCH /v1/users/:id` (strict — these are the ONLY accepted keys).
 * NOT updatable here: `email`, `password`, role/permissions (use
 * `assignRole`). `regionId` is rejected (422) for external users (§D.3).
 */
export type UpdateUserBody = {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  companyName?: string | null;
  companyAddress?: string | null;
  departmentId?: number | null;
  regionId?: number;
  isActive?: boolean;
};

export async function updateUser(
  id: string,
  body: UpdateUserBody,
): Promise<AdminUser> {
  const res = await Api.patch(`/v1/users/${id}`, body);
  return unwrap<AdminUser>(res.data);
}

/**
 * `POST /v1/users/:id/role` — changes a user's role by stable role code.
 * Permissions follow the role (§D.1); there is no per-user permission edit.
 */
export async function assignRole(
  id: string,
  roleCode: RoleCode,
): Promise<AdminUser> {
  const res = await Api.post(`/v1/users/${id}/role`, { roleCode });
  return unwrap<AdminUser>(res.data);
}

/**
 * `DELETE /v1/users/:id` — removes the user's Cognito identity AND soft-deletes
 * the DB row. The user disappears from the list (active-only) and can no longer
 * sign in; re-registration with the same email reactivates the account. Returns
 * 204 No Content. 409 `cannot-delete-self` if you target your own account.
 */
export async function deleteUser(id: string): Promise<void> {
  await Api.delete(`/v1/users/${id}`);
}

// ---------------------------------------------------------------------------
// Self-service profile — `GET` / `PATCH /v1/users/me` (svc-03). Any
// authenticated user may read and edit their OWN record. The PATCH body is
// strict (`UpdateSelfSchema`): it can NEVER change email, role, region (§D.3),
// or active state — those are read-only here.
// ---------------------------------------------------------------------------

/** Effective cost assumptions returned alongside the user (unused by the admin
 *  profile screen, kept for contract parity with svc-03 `MeResponse`). */
export type UserAssumptionsDto = {
  laborRate?: string;
  daysPerYear?: string;
  hoursPerDay?: string;
  scrapManual?: string;
  scrapAuto?: string;
  reworkCost?: string;
  oeeManual?: string;
  oeeAuto?: string;
  usefulLifeYears?: string;
};

/** Wire shape of `GET /v1/users/me`. */
export type MeResponse = {
  user: AuthUser;
  assumptions: UserAssumptionsDto | null;
};

export async function fetchMe(): Promise<MeResponse> {
  // forwardUserToken: the shared demographic subset is overlaid from the central
  // directory (svc-03 relays the issuer id_token to the registration-api).
  const res = await Api.get("/v1/users/me", { forwardUserToken: true });
  return unwrap<MeResponse>(res.data);
}

/** Body of `PATCH /v1/users/me` — the only self-editable fields. */
export type UpdateMyProfileInput = {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  companyName?: string | null;
  companyAddress?: string | null;
  departmentId?: number | null;
};

export async function updateMyProfile(
  body: UpdateMyProfileInput,
): Promise<AuthUser> {
  // forwardUserToken: shared-field edits are written through to the central
  // directory before the local write; without the token the shared edit is refused.
  const res = await Api.patch("/v1/users/me", body, { forwardUserToken: true });
  return unwrap<AuthUser>(res.data);
}
